/**
 * OnboardingWizard — 5/6-step onboarding flow.
 *   1 Brand Basics (parent co, website, socials, vertical, logo, voice, safety pledge)
 *   2 Multi-Brand Portfolio (conditional)
 *   3 Unlock Your Footprint (CRM / retailers / stores)
 *   4 Products & Core Audience (per-SKU audience + competitors + brand-level conversion tracking)
 *   5 Subscription & Wallet
 *   6 Team Invites + Summary + Confetti
 *
 * Dark theme #0A0A0A / #121212, teal #00D4FF, pink #FF4D94, Inter font.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { useAuth } from '../contexts/AuthContext';
import {
  Waves, Check, Sparkles, Upload, ChevronRight, ArrowLeft, Loader2, CreditCard,
  Wallet, Shield, Rocket, Building2, Plus, Trash2, FileSpreadsheet, AtSign,
  UserPlus, Mail, Link2, MapPin, Database, Store, ChevronDown, X,
  TrendingUp, Network, PenLine, Package, Users,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const VERTICALS = [
  'Health & Wellness', 'Beauty', 'CPG / Grocery', 'Fashion', 'Food & Beverage',
  'Tech & Gadgets', 'Fitness', 'Financial Services', 'Travel', 'Automotive',
  'Entertainment', 'Gaming', 'Home & Garden', 'Other',
];

const PLEDGE_VERSION = '1.0';
const PLEDGE_CLAUSES = [
  {
    title: 'Product legality & FTC / FDA compliance',
    body: 'All products I promote through WAVE comply with FTC, FDA, and applicable regulatory requirements in the geos where my Waves run. Any health, financial, or legal claims made in creator briefs are substantiated.',
  },
  {
    title: 'No prohibited categories',
    body: 'My brand does not operate in firearms, tobacco / vape (outside approved partners), gambling (outside licensed geos), adult content, MLM / pyramid schemes, or political campaigning.',
  },
  {
    title: 'Truthful representations to creators',
    body: 'All product descriptions, ingredient lists, performance claims, and pricing provided to creators are accurate as of the campaign date.',
  },
  {
    title: 'Creator editorial independence',
    body: 'Creators retain final editorial control of their posts. I will not require creators to violate platform terms or FTC #ad disclosure rules as a condition of payment.',
  },
  {
    title: 'Lawful data & privacy',
    body: 'Any customer data I upload (CRM lists, retail partner lists, store addresses) is lawfully obtained and I have the right to share it with WAVE for creator-matching models.',
  },
  {
    title: 'Payment & good-faith engagement',
    body: 'I will honor agreed payment terms within 14 days of post delivery and will not retaliate against creators for legitimate feedback or campaign outcomes.',
  },
];

const GENDERS = [{ id: 'female', label: 'Female' }, { id: 'male', label: 'Male' }, { id: 'nonbinary', label: 'Non-binary' }];
const GEOS = ['US', 'CA', 'UK', 'AU', 'EU', 'LATAM', 'APAC', 'Global'];

const FOOTPRINT_KINDS = [
  { id: 'customer_segments',      icon: Database, title: 'Customer Segments',
    desc: 'Upload existing customers (segmented by tier or LTV). Gives the caption engine real customer context for every RFP. Staged: lookalike creator matching.' },
  { id: 'performance_history',    icon: TrendingUp, title: 'Performance History',
    desc: 'Prior campaign results. Teaches the system what worked best for you so future waves perform better.' },
  { id: 'geographic_performance', icon: MapPin,   title: 'Geographic Performance',
    desc: 'Per-state stores, customers, revenue, ROAS. Powers the near-me Discover filter and location-aware captions. Staged: geo-weighted matching.' },
  { id: 'audience_overlap',       icon: Network,  title: 'Audience Overlap Data',
    desc: 'Pairwise audience overlap with creators you know. Adds overlap context to your wave briefs. Staged: cohort diversity-scoring integration.' },
  { id: 'creative_insights',      icon: PenLine,  title: 'Creative Insights',
    desc: 'Angles and sample copy that historically performed. Gives smarter angle and caption recommendations in the SKU modal.' },
  { id: 'sku_performance',        icon: Package,  title: 'SKU Performance Data',
    desc: 'Product-level results per SKU. Surfaces your product history in the SKU recommendations panel.' },
  { id: 'observed_demographics',  icon: Users,    title: 'Customer Demographics (Aggregated)',
    desc: 'Pre-aggregated age/gender/state counts of your real customers. Raises the observed share of your brand audience vector — the audience-matching channel runs on real demographics, not just declared targets. Individual-identifying data (emails, names, DOBs, zips) is rejected at upload.' },
];

const TEAM_ROLES = [
  { id: 'admin',         label: 'Admin',         desc: 'Full access, subscription + billing.' },
  { id: 'brand_manager', label: 'Brand Manager', desc: 'Plan/send Waves, invite creators.' },
  { id: 'finance',       label: 'Finance',       desc: 'Billing, invoices, wallet top-up.' },
  { id: 'viewer',        label: 'Viewer',        desc: 'Read-only Insights + Live Dashboards.' },
];

export default function OnboardingWizard() {
  const nav = useNavigate();
  const { user, refreshUser } = useAuth();
  const [step, setStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [dnaSpinner, setDnaSpinner] = useState(false);

  // ─── Step 1 ───
  const [brandName, setBrandName] = useState('');
  const [parentCompany, setParentCompany] = useState('');
  const [websiteUrl, setWebsiteUrl] = useState('');
  const [socials, setSocials] = useState({ tiktok: '', instagram: '', youtube: '' });
  const [vertical, setVertical] = useState('');
  const [brandScale, setBrandScale] = useState('');  // single_location | multi_location | dtc_national | enterprise_national
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoRemoteUrl, setLogoRemoteUrl] = useState(null);
  const [managesMulti, setManagesMulti] = useState(false);
  const [safetyPledge, setSafetyPledge] = useState(false);

  // ─── Step 2 ───
  const [subBrands, setSubBrands] = useState([]);

  // ─── Step 3 footprint ───
  const [footprintAssets, setFootprintAssets] = useState([]);  // [{kind, filename, bytes}]

  // ─── Step 4 ───
  const [products, setProducts] = useState([]);  // each: {sku, name, description, category, core_audience:{age_range,genders,geos}, competitors:[]}
  const [shoppableUrl, setShoppableUrl] = useState('');
  const [utmBase, setUtmBase] = useState('');
  const [pixelId, setPixelId] = useState('');

  // ─── Step 5 ───
  const [plans, setPlans] = useState(null);
  const [chosenTier, setChosenTier] = useState(null);
  const [chosenTopup, setChosenTopup] = useState(null);
  const [customAmount, setCustomAmount] = useState('');

  // ─── Step 6 ───
  const [invites, setInvites] = useState([{ _rid: makeRowId(), email: '', role: 'brand_manager' }]);

  const confettiDone = useRef(false);

  // Step index mapping (internal step number → UI index)
  const totalSteps = managesMulti ? 6 : 5;
  // Internal order: 1 basics, 2 portfolio, 3 footprint, 4 products, 5 sub, 6 team
  // UI order (no multi): skip step 2 — so internal [1,3,4,5,6] → UI [1..5]
  const uiStep = managesMulti ? step : (step >= 2 ? step - 1 : step);

  useEffect(() => {
    if (!user) return;
    if (user.onboarding_complete) { nav('/home', { replace: true }); return; }
    setBrandName(prev => prev || user.brand_name || '');
    axios.get(`${API}/api/payments/plans`)
      .then(r => setPlans(r.data))
      .catch(e => {
        if (process.env.NODE_ENV !== 'production') {
          console.debug('plans fetch failed:', e?.response?.status || e?.message);
        }
      });
    // API + axios are module-level stable refs; React setters are stable.
    // Only `user` + `nav` drive re-runs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, nav]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const sessionId = url.searchParams.get('session_id');
    if (!sessionId) return;
    let tries = 0;
    const poll = async () => {
      tries += 1;
      try {
        const r = await axios.get(`${API}/api/payments/checkout/status/${sessionId}`, { withCredentials: true });
        if (r.data.payment_status === 'paid') {
          await refreshUser();
          url.searchParams.delete('session_id');
          window.history.replaceState({}, '', url.toString());
          setStep(6);
          return;
        }
      } catch (e) {
        if (process.env.NODE_ENV !== 'production') {
          // Best-effort — Stripe occasionally needs a moment to report paid.
          console.debug('checkout status poll retry:', e?.response?.status || e?.message);
        }
      }
      if (tries < 10) setTimeout(poll, 1500);
    };
    poll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (step === 6 && !confettiDone.current) {
      confettiDone.current = true;
      fireConfetti();
    }
  }, [step]);

  const logoUpload = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2 * 1024 * 1024) { setError('Logo too large — 2 MB max.'); return; }
    const reader = new FileReader();
    reader.onload = () => setLogoPreview(reader.result);
    reader.readAsDataURL(f);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await axios.post(`${API}/api/brand/onboarding/upload-logo`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      setLogoRemoteUrl(r.data.logo_url);
    } catch (err) { setError(err?.response?.data?.detail || 'Logo upload failed.'); }
  };

  const saveStep1 = async () => {
    if (!brandName.trim() || !vertical || !brandScale || !safetyPledge) {
      setError('Brand name, vertical, scale, and safety pledge are required.');
      return;
    }
    setSaving(true); setError(null); setDnaSpinner(true);
    try {
      const r = await axios.post(`${API}/api/brand/onboarding/step1`, {
        brand_name: brandName.trim(),
        parent_company: parentCompany.trim() || null,
        website_url: websiteUrl.trim() || null,
        social_handles: socials, vertical,
        brand_scale: brandScale,
        logo_url: logoRemoteUrl || null,
        manages_multiple_brands: managesMulti,
        brand_safety_pledge: true,
        brand_safety_pledge_version: PLEDGE_VERSION,
      }, { withCredentials: true });
      await new Promise(res => setTimeout(res, 1800));
      setDnaSpinner(false);
      setStep(r.data.next_step);  // → 2 if multi, 3 otherwise
    } catch (e) {
      setError(e.response?.data?.detail || 'Could not save.');
      setDnaSpinner(false);
    } finally { setSaving(false); }
  };

  const saveStep2 = async () => {
    setSaving(true); setError(null);
    try {
      await axios.post(`${API}/api/brand/onboarding/step2`, { sub_brands: subBrands }, { withCredentials: true });
      setStep(3);
    } catch (e) { setError(e.response?.data?.detail || 'Could not save portfolio.'); }
    finally { setSaving(false); }
  };

  const saveStep3 = async () => {
    setSaving(true); setError(null);
    try {
      await axios.post(`${API}/api/brand/onboarding/step3`,
        { skipped: footprintAssets.length === 0 }, { withCredentials: true });
      setStep(4);
    } catch (e) { setError(e.response?.data?.detail || 'Could not save.'); }
    finally { setSaving(false); }
  };

  const saveStep4 = async () => {
    setSaving(true); setError(null);
    try {
      await axios.post(`${API}/api/brand/onboarding/step4`, {
        products,
        conversion_tracking: {
          shoppable_url: shoppableUrl.trim() || null,
          utm_base: utmBase.trim() || null,
          pixel_id: pixelId.trim() || null,
        },
      }, { withCredentials: true });
      setStep(5);
    } catch (e) { setError(e.response?.data?.detail || 'Could not save.'); }
    finally { setSaving(false); }
  };

  const subscribe = async (tierId) => {
    setSaving(true); setError(null);
    try {
      const r = await axios.post(`${API}/api/payments/subscribe`,
        { tier: tierId, origin_url: window.location.origin + '/onboarding' },
        { withCredentials: true });
      window.location.href = r.data.url;
    } catch (e) { setError(e.response?.data?.detail || 'Could not start checkout.'); setSaving(false); }
  };

  const topup = async () => {
    const payload = { origin_url: window.location.origin + '/onboarding' };
    if (chosenTopup) payload.package_id = chosenTopup;
    else if (customAmount) payload.custom_amount = parseFloat(customAmount);
    else { setError('Pick a top-up amount.'); return; }
    setSaving(true); setError(null);
    try {
      const r = await axios.post(`${API}/api/payments/wallet/topup`, payload, { withCredentials: true });
      window.location.href = r.data.url;
    } catch (e) { setError(e.response?.data?.detail || 'Could not start top-up.'); setSaving(false); }
  };

  const completeAndGo = async () => {
    setSaving(true);
    try {
      const validInvites = invites.filter(i => i.email.includes('@'));
      if (validInvites.length > 0) {
        await axios.post(`${API}/api/brand/onboarding/team/invite`, { invites: validInvites }, { withCredentials: true });
      }
      await axios.post(`${API}/api/brand/onboarding/complete`, {}, { withCredentials: true });
      await refreshUser();
      nav('/home', { replace: true });
    } catch (e) { setError(e.response?.data?.detail || 'Could not complete onboarding.'); setSaving(false); }
  };

  const back = () => {
    if (step === 3 && !managesMulti) setStep(1);
    else setStep(s => Math.max(1, s - 1));
  };

  const feeRate = plans?.platform_fee_rate ?? 0.1;

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white py-10 px-4" data-testid="onboarding-wizard">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-9 h-9 rounded-xl bg-[#00D4FF]/15 flex items-center justify-center">
            <Waves className="h-5 w-5 text-[#00D4FF]" />
          </div>
          <span className="text-xl font-black">WAVE</span>
          <span className="text-[9px] text-gray-500 uppercase tracking-widest mt-1">Onboarding</span>
        </div>

        <StepProgress currentUi={uiStep} totalSteps={totalSteps} managesMulti={managesMulti} />

        {dnaSpinner && <DigitalDnaBuildingOverlay />}

        <div className="mt-6 rounded-2xl bg-[#121212] border border-[#1f1f1f] p-7 lg:p-10" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.45)' }}>
          {step === 1 && (
            <Step1 {...{
              brandName, setBrandName, parentCompany, setParentCompany, websiteUrl, setWebsiteUrl,
              socials, setSocials, vertical, setVertical, logoPreview, logoUpload,
              brandScale, setBrandScale,
              managesMulti, setManagesMulti,
              safetyPledge, setSafetyPledge,
            }} />
          )}
          {step === 2 && <Step2 {...{ subBrands, setSubBrands }} />}
          {step === 3 && <Step3Footprint {...{ footprintAssets, setFootprintAssets, setError }} />}
          {step === 4 && <Step4Products {...{ products, setProducts, shoppableUrl, setShoppableUrl, utmBase, setUtmBase, pixelId, setPixelId }} />}
          {step === 5 && (
            <Step5Sub {...{
              plans, feeRate, chosenTier, setChosenTier, chosenTopup, setChosenTopup,
              customAmount, setCustomAmount, subscribe, topup,
              subscription: user?.subscription_status, walletBalance: user?.wallet_balance,
            }} />
          )}
          {step === 6 && (
            <Step6Team {...{
              invites, setInvites, user,
              summary: { brandName, parentCompany, websiteUrl, vertical, logoPreview,
                         subBrands: managesMulti ? subBrands : [], products, footprintAssets,
                         shoppableUrl, utmBase, pixelId },
            }} />
          )}

          {error && (
            <div className="mt-5 text-[11px] text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg p-3" data-testid="onboarding-error">
              {error}
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            {step > 1 && step < 6 ? (
              <button onClick={back} data-testid="onboarding-back-btn"
                      className="text-xs text-gray-400 hover:text-white flex items-center gap-1">
                <ArrowLeft className="h-3 w-3" /> Back
              </button>
            ) : <span />}

            {step === 1 && <NextBtn testId="onboarding-next-1" onClick={saveStep1} loading={saving} />}
            {step === 2 && <NextBtn testId="onboarding-next-2" onClick={saveStep2} loading={saving} />}
            {step === 3 && <NextBtn testId="onboarding-next-3" onClick={saveStep3} loading={saving} label={footprintAssets.length === 0 ? 'Skip for now' : 'Continue'} />}
            {step === 4 && <NextBtn testId="onboarding-next-4" onClick={saveStep4} loading={saving} />}
            {step === 5 && (
              <button onClick={() => setStep(6)} data-testid="onboarding-skip-to-6"
                      className="text-[10px] text-gray-500 hover:text-gray-300">Skip for now →</button>
            )}
            {step === 6 && (
              <button onClick={completeAndGo} disabled={saving} data-testid="onboarding-complete-btn"
                      className="px-8 py-3.5 rounded-lg bg-[#FF4D94] text-white font-bold text-sm flex items-center gap-2 hover:brightness-110 hover:scale-[1.02] transition-all disabled:opacity-60"
                      style={{ boxShadow: '0 0 32px rgba(255,77,148,0.55)' }}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Rocket className="h-4 w-4" /> Complete Onboarding & Launch First Wave</>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function NextBtn({ onClick, loading, testId, label }) {
  return (
    <button onClick={onClick} disabled={loading} data-testid={testId}
            className="px-7 py-3 rounded-lg bg-[#FF4D94] text-white font-bold text-xs flex items-center gap-2 hover:brightness-110 hover:scale-[1.02] transition-all disabled:opacity-60"
            style={{ boxShadow: '0 0 20px rgba(255,77,148,0.35)' }}>
      {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <>{label || 'Continue'} <ChevronRight className="h-3.5 w-3.5" /></>}
    </button>
  );
}

function StepProgress({ currentUi, totalSteps, managesMulti }) {
  const labels = managesMulti
    ? ['Brand Basics', 'Portfolio', 'Footprint', 'Products', 'Subscription', 'Team & Confirm']
    : ['Brand Basics', 'Footprint', 'Products', 'Subscription', 'Team & Confirm'];
  return (
    <div className="flex items-center gap-2" data-testid="onboarding-progress">
      {labels.map((label, i) => {
        const n = i + 1;
        const state = currentUi > n ? 'done' : currentUi === n ? 'active' : 'pending';
        return (
          <React.Fragment key={label}>
            <div className="flex items-center gap-2">
              <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-black transition-all ${
                state === 'done' ? 'bg-[#00D4FF] text-black'
                : state === 'active' ? 'bg-[#FF4D94] text-white animate-pulse'
                : 'bg-[#1f1f1f] text-gray-500'
              }`} style={state === 'active' ? { boxShadow: '0 0 16px rgba(255,77,148,0.6)' } : undefined}>
                {state === 'done' ? <Check className="h-3.5 w-3.5" /> : n}
              </div>
              <span className={`text-[10px] font-bold uppercase tracking-wider hidden sm:block ${state === 'pending' ? 'text-gray-600' : 'text-white'}`}>{label}</span>
            </div>
            {i < totalSteps - 1 && <div className={`flex-1 h-px ${state === 'done' ? 'bg-[#00D4FF]' : 'bg-[#1f1f1f]'}`} />}
          </React.Fragment>
        );
      })}
    </div>
  );
}

function DigitalDnaBuildingOverlay() {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 backdrop-blur-sm" data-testid="digital-dna-overlay">
      <div className="text-center space-y-4">
        <div className="relative w-20 h-20 mx-auto">
          <div className="absolute inset-0 rounded-full border-2 border-[#00D4FF] animate-ping opacity-50" />
          <div className="absolute inset-2 rounded-full border-2 border-[#FF4D94] animate-ping opacity-50" style={{ animationDelay: '0.3s' }} />
          <div className="absolute inset-0 flex items-center justify-center">
            <Sparkles className="h-8 w-8 text-[#00D4FF] animate-pulse" />
          </div>
        </div>
        <p className="text-sm text-white font-black">Building your Digital DNA™…</p>
        <p className="text-[11px] text-gray-500 max-w-xs">Scoring your brand against 4,200+ creators across vertical, audience overlap, and tone match.</p>
      </div>
    </div>
  );
}

function fireConfetti() {
  const duration = 2800;
  const end = Date.now() + duration;
  const colors = ['#FF4D94', '#00D4FF', '#ffffff'];
  (function frame() {
    confetti({ particleCount: 4, angle: 60, spread: 55, origin: { x: 0 }, colors });
    confetti({ particleCount: 4, angle: 120, spread: 55, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
}

function makeRowId() { return `row_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`; }

const inputCls = "w-full px-4 py-3 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] focus:border-[#00D4FF] text-sm outline-none transition-all";

function Field({ label, required, icon: Icon, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-1.5 flex items-center gap-1">
        {Icon && <Icon className="h-3 w-3" />}
        {label} {required && <span className="text-[#FF4D94]">*</span>}
      </label>
      {children}
    </div>
  );
}

function Section({ title, subtitle, icon: Icon, children }) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-3 flex-wrap">
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold flex items-center gap-1.5">
          {Icon && <Icon className="h-3 w-3" />} {title}
        </p>
        {subtitle && <span className="text-[10px] text-gray-600">— {subtitle}</span>}
      </div>
      {children}
    </div>
  );
}

/* ═══════════════ STEP 1 — BRAND BASICS ═══════════════ */
function Step1(p) {
  return (
    <div className="space-y-6" data-testid="onboarding-step-1">
      <div>
        <h1 className="text-2xl font-black mb-1">Let&apos;s set up your brand</h1>
        <p className="text-sm text-gray-500">A few basics. We&apos;ll feed these into your Digital DNA™ match in the background.</p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-6">
        <div className="space-y-4">
          <Field label="Brand Name" required>
            <input value={p.brandName} onChange={e => p.setBrandName(e.target.value)} data-testid="step1-brand-name"
                   placeholder="Your brand name" className={inputCls} />
          </Field>
          <Field label="Parent / Holding Company" icon={Building2}>
            <input value={p.parentCompany} onChange={e => p.setParentCompany(e.target.value)} data-testid="step1-parent-co"
                   placeholder="Optional — your parent or holding company" className={inputCls} />
          </Field>
          <Field label="Website URL">
            <input value={p.websiteUrl} onChange={e => p.setWebsiteUrl(e.target.value)} data-testid="step1-website"
                   placeholder="https://" className={inputCls} />
          </Field>
          <div>
            <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2 block">Primary Social Handles</label>
            <div className="space-y-2">
              {['tiktok', 'instagram', 'youtube'].map(pl => (
                <div key={pl} className="flex items-center gap-2">
                  <span className="text-[10px] w-16 text-gray-500 uppercase font-semibold">{pl}</span>
                  <div className="flex-1 flex items-center gap-1.5 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] focus-within:border-[#00D4FF] px-3">
                    <AtSign className="h-3 w-3 text-gray-600" />
                    <input value={p.socials[pl]} onChange={e => p.setSocials({ ...p.socials, [pl]: e.target.value })}
                           data-testid={`step1-social-${pl}`}
                           placeholder={`your handle on ${pl}`} className="bg-transparent outline-none text-sm py-2.5 w-full" />
                  </div>
                </div>
              ))}
            </div>
          </div>
          <Field label="Vertical / Industry" required>
            <select value={p.vertical} onChange={e => p.setVertical(e.target.value)} data-testid="step1-vertical" className={inputCls}>
              <option value="">Pick one…</option>
              {VERTICALS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </Field>
          <BrandScalePicker value={p.brandScale} onChange={p.setBrandScale} />
        </div>
        <div className="flex flex-col items-center gap-2">
          <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">Logo</label>
          <label htmlFor="logo-upload" className="relative w-28 h-28 rounded-full bg-[#0A0A0A] border-2 border-dashed border-[#1f1f1f] hover:border-[#00D4FF] flex items-center justify-center cursor-pointer transition-all"
                 data-testid="step1-logo-upload">
            {p.logoPreview ? (
              <img src={p.logoPreview} alt="Logo preview" className="w-full h-full rounded-full object-cover" />
            ) : (
              <div className="text-center">
                <Upload className="h-5 w-5 text-gray-600 mx-auto" />
                <span className="text-[9px] text-gray-600 mt-1 block">Drop / click</span>
              </div>
            )}
          </label>
          <input id="logo-upload" type="file" accept="image/*" className="hidden" onChange={p.logoUpload} />
          <span className="text-[9px] text-gray-600">Max 2 MB</span>
        </div>
      </div>

      <div>
        {/* Brand Voice was here — intentionally removed per product feedback */}
      </div>

      <label className="flex items-center gap-3 rounded-xl p-4 bg-[#0A0A0A] border border-[#1f1f1f] hover:border-[#00D4FF]/40 cursor-pointer transition-all"
             data-testid="step1-multi-toggle">
        <input type="checkbox" checked={p.managesMulti} onChange={e => p.setManagesMulti(e.target.checked)} className="w-4 h-4 accent-[#00D4FF]" />
        <div className="flex-1">
          <p className="text-xs font-semibold text-white">I manage multiple brands / sub-brands</p>
          <p className="text-[10px] text-gray-500">Manage multiple sub-brands under one account. You&apos;ll add each one next.</p>
        </div>
      </label>

      <BrandSafetyPledgeBlock value={p.safetyPledge} onChange={p.setSafetyPledge} />
    </div>
  );
}

function BrandScalePicker({ value, onChange }) {
  // This single field drives ~15 downstream defaults (Home hero copy, Build
  // Wave geo default, Discover radius filter, creator-scale presets, etc.).
  // We capture it once in onboarding and let the rest of the product adapt.
  const opts = [
    { id: 'single_location',     title: 'Single location',       sub: '1 store / studio / dispensary',          icon: MapPin },
    { id: 'multi_location',      title: 'Multi-location',        sub: '2-50 stores in a region',                icon: Store },
    { id: 'dtc_national',        title: 'DTC / National',        sub: 'Ship nationwide, no physical stores',    icon: Rocket },
    { id: 'enterprise_national', title: 'Enterprise / Retail',   sub: 'National retailer footprint (beauty + mass retail)', icon: Building2 },
  ];
  return (
    <div data-testid="step1-brand-scale">
      <label className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-2 flex items-center gap-1">
        Brand Scale <span className="text-[#FF4D94]">*</span>
        <span className="text-[9px] text-gray-600 normal-case tracking-normal ml-1">— how big is your commercial footprint today?</span>
      </label>
      <div className="grid grid-cols-2 gap-2">
        {opts.map(o => {
          const Icon = o.icon;
          const active = value === o.id;
          return (
            <button
              key={o.id}
              type="button"
              data-testid={`step1-brand-scale-${o.id}`}
              onClick={() => onChange(o.id)}
              className={`text-left p-3 rounded-xl border transition-all ${
                active
                  ? 'border-[#FF4D94] bg-[#FF4D94]/10'
                  : 'border-[#1f1f1f] bg-[#0A0A0A] hover:border-[#00D4FF]/50'
              }`}
              style={active ? { boxShadow: '0 0 12px rgba(255,77,148,0.25)' } : undefined}
            >
              <div className="flex items-center gap-2 mb-1">
                <Icon className={`h-3.5 w-3.5 ${active ? 'text-[#FF4D94]' : 'text-[#00D4FF]'}`} />
                <p className="text-[11px] font-black text-white">{o.title}</p>
              </div>
              <p className="text-[10px] text-gray-500 leading-snug">{o.sub}</p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function BrandSafetyPledgeBlock({ value, onChange }) {
  const [modalOpen, setModalOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  return (
    <>
      <div className="rounded-xl bg-[#00D4FF]/5 border border-[#00D4FF]/20 p-4" data-testid="step1-safety-pledge-block">
        <div className="flex items-start gap-3">
          <Shield className="h-5 w-5 text-[#00D4FF] flex-shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline justify-between gap-2 flex-wrap">
              <p className="text-xs font-bold text-white">Brand Safety Pledge — v{PLEDGE_VERSION}</p>
              <button onClick={() => setModalOpen(true)} data-testid="step1-pledge-open"
                      className="text-[11px] text-[#00D4FF] font-semibold hover:underline flex items-center gap-0.5">
                Read full pledge <ChevronRight className="h-3 w-3" />
              </button>
            </div>
            <p className="text-[11px] text-gray-400 mt-1 leading-relaxed">
              By accepting, you confirm your brand is <span className="text-white">FTC/FDA compliant</span>,
              operates <span className="text-white">outside prohibited categories</span> (firearms, adult,
              MLM, etc.), makes <span className="text-white">truthful product claims</span>,
              respects <span className="text-white">creator editorial independence</span>, uses only <span className="text-white">lawfully-obtained data</span>,
              and pays creators <span className="text-white">within 14 days</span>.
            </p>
            <button onClick={() => setExpanded(x => !x)} data-testid="step1-pledge-expand"
                    className="mt-2 text-[10px] text-gray-500 hover:text-white flex items-center gap-1">
              <ChevronDown className={`h-3 w-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
              {expanded ? 'Hide summary' : 'Show 6-point summary inline'}
            </button>
            {expanded && (
              <ul className="mt-2 space-y-1.5 text-[11px] text-gray-400 leading-relaxed list-disc ml-4" data-testid="step1-pledge-summary">
                {PLEDGE_CLAUSES.map(c => (
                  <li key={c.title}><span className="text-white">{c.title}.</span> {c.body}</li>
                ))}
              </ul>
            )}
            <label className="flex items-start gap-2.5 cursor-pointer mt-3 pt-3 border-t border-[#00D4FF]/15" data-testid="step1-safety-pledge">
              <input type="checkbox" checked={value} onChange={e => onChange(e.target.checked)} className="mt-0.5 w-4 h-4 accent-[#00D4FF]" />
              <span className="text-[11px] text-white">I have read and accept the WAVE Brand Safety Pledge v{PLEDGE_VERSION}.</span>
            </label>
          </div>
        </div>
      </div>
      {modalOpen && <PledgeModal onClose={() => setModalOpen(false)} />}
    </>
  );
}

function PledgeModal({ onClose }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm p-4" onClick={onClose} data-testid="pledge-modal">
      <div onClick={e => e.stopPropagation()} className="bg-[#121212] border border-[#1f1f1f] rounded-2xl p-6 max-w-2xl w-full max-h-[85vh] overflow-y-auto" style={{ boxShadow: '0 20px 60px rgba(0,0,0,0.6)' }}>
        <div className="flex items-start justify-between gap-4 mb-5">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[#00D4FF] font-bold">WAVE Trust Framework</p>
            <h2 className="text-xl font-black text-white mt-1">Brand Safety Pledge — v{PLEDGE_VERSION}</h2>
          </div>
          <button onClick={onClose} data-testid="pledge-modal-close" className="w-8 h-8 rounded-full bg-[#1f1f1f] hover:bg-[#2a2a2a] flex items-center justify-center text-gray-400">
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-xs text-gray-400 leading-relaxed mb-4">By accepting the WAVE Brand Safety Pledge you make the following commitments. These protect creators, protect the platform, and ensure every Wave runs on trustworthy ground.</p>
        <ol className="space-y-3">
          {PLEDGE_CLAUSES.map((c, i) => (
            <li key={c.title} className="flex gap-3">
              <div className="w-6 h-6 rounded-full bg-[#00D4FF]/15 text-[#00D4FF] text-[11px] font-black flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</div>
              <div>
                <p className="text-sm font-bold text-white mb-0.5">{c.title}</p>
                <p className="text-[11px] text-gray-400 leading-relaxed">{c.body}</p>
              </div>
            </li>
          ))}
        </ol>
        <div className="mt-5 pt-4 border-t border-[#1f1f1f] text-[10px] text-gray-500 leading-relaxed">
          Brand-safety violations can result in suspension or termination per the WAVE Terms of Service.
          Pledge version <span className="text-gray-300 font-semibold">v{PLEDGE_VERSION}</span> · effective 2026-02-21.
        </div>
      </div>
    </div>
  );
}

/* ═══════════════ STEP 2 — MULTI-BRAND PORTFOLIO ═══════════════ */
function Step2({ subBrands, setSubBrands }) {
  const add = () => setSubBrands([...subBrands, { _rid: makeRowId(), brand_name: '', website_url: '', logo_url: '' }]);
  const update = (i, field, val) => { const n = [...subBrands]; n[i] = { ...n[i], [field]: val }; setSubBrands(n); };
  const remove = (i) => setSubBrands(subBrands.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-6" data-testid="onboarding-step-2">
      <div>
        <h1 className="text-2xl font-black mb-1">Your Brand Portfolio</h1>
        <p className="text-sm text-gray-500">Add every brand you want to run Waves for. You can add more later.</p>
      </div>
      {subBrands.length === 0 && (
        <div className="rounded-xl bg-[#0A0A0A] border border-dashed border-[#1f1f1f] py-10 text-center">
          <Building2 className="h-8 w-8 text-gray-700 mx-auto mb-2" />
          <p className="text-xs text-gray-500">No brands yet. Click below to add your first sub-brand.</p>
        </div>
      )}
      <div className="space-y-2">
        {subBrands.map((sb, i) => (
          <div key={sb._rid || sb.brand_name || i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center" data-testid={`subbrand-${i}`}>
            <input value={sb.brand_name} onChange={e => update(i, 'brand_name', e.target.value)} placeholder="Brand name" className={inputCls} />
            <input value={sb.website_url} onChange={e => update(i, 'website_url', e.target.value)} placeholder="https://" className={inputCls} />
            <button onClick={() => remove(i)} className="w-10 h-10 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] hover:border-red-500/50 text-red-400 flex items-center justify-center" data-testid={`subbrand-remove-${i}`}>
              <Trash2 className="h-3.5 w-3.5" />
            </button>
          </div>
        ))}
      </div>
      <button type="button" onClick={add} data-testid="subbrand-add"
              className="w-full py-3 rounded-xl border-2 border-dashed border-[#1f1f1f] hover:border-[#00D4FF] hover:bg-[#00D4FF]/5 text-xs font-semibold text-gray-400 hover:text-white flex items-center justify-center gap-2 transition-all">
        <Plus className="h-4 w-4" /> Add Another Brand
      </button>
    </div>
  );
}

/* ═══════════════ STEP 3 — UNLOCK YOUR FOOTPRINT ═══════════════ */
function Step3Footprint({ footprintAssets, setFootprintAssets, setError }) {
  return (
    <div className="space-y-6" data-testid="onboarding-step-3">
      <div>
        <h1 className="text-2xl font-black mb-1">Your 1st Party Data Room</h1>
        <p className="text-sm text-gray-500">
          Upload your private brand data once.{' '}
          <span className="text-white">Get smarter creator matching, better waves, and higher performance forever.</span>
        </p>
      </div>
      <div className="rounded-xl p-4 bg-gradient-to-br from-[#A78BFA]/5 to-[#FF4D94]/5 border border-[#A78BFA]/20 flex items-start gap-3" data-testid="footprint-why">
        <Sparkles className="h-5 w-5 text-[#A78BFA] flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-semibold text-white mb-1">What this unlocks</p>
          <ul className="text-[11px] text-gray-400 leading-relaxed space-y-0.5 list-disc ml-4">
            <li><span className="text-white">Hyper-local boost</span> — creators near your top geos get +2× weight</li>
            <li><span className="text-white">Lookalike matching</span> — creators whose audiences resemble your customer segments</li>
            <li><span className="text-white">Smarter captions + angles</span> — auto-tuned to what's historically worked for your brand</li>
          </ul>
        </div>
      </div>

      <div className="space-y-3">
        {FOOTPRINT_KINDS.map(k => (
          <FootprintUploadRow key={k.id} kind={k} existing={footprintAssets.find(a => a.kind === k.id)}
                              onUploaded={(asset) => setFootprintAssets([...footprintAssets.filter(a => a.kind !== k.id), asset])}
                              onRemove={() => setFootprintAssets(footprintAssets.filter(a => a.kind !== k.id))}
                              onError={setError} />
        ))}
      </div>

      <p className="text-[11px] text-gray-600 text-center">You can always upload or update your 1st party data later in <span className="text-gray-400">1st Party Data Room</span>.</p>
    </div>
  );
}

function FootprintUploadRow({ kind, existing, onUploaded, onRemove, onError }) {
  const ref = useRef(null);
  const [uploading, setUploading] = useState(false);
  const Icon = kind.icon;

  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 20 * 1024 * 1024) { onError?.('File too large (max 20 MB)'); return; }
    setUploading(true);
    const fd = new FormData();
    fd.append('file', f);
    try {
      const r = await axios.post(`${API}/api/brand/data-room/upload/${kind.id}`, fd, {
        withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' },
      });
      onUploaded({ kind: kind.id, filename: f.name, bytes: f.size, preview_label: r.data.preview_label });
    } catch (err) { onError?.(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  return (
    <div className={`rounded-xl p-4 border transition-all ${existing ? 'bg-[#4ade8010] border-[#4ade8040]' : 'bg-[#0A0A0A] border-[#1f1f1f] hover:border-[#00D4FF]/40'}`}
         data-testid={`footprint-row-${kind.id}`}>
      <div className="flex items-start gap-3">
        <Icon className={`h-5 w-5 flex-shrink-0 mt-0.5 ${existing ? 'text-green-400' : 'text-[#00D4FF]'}`} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-black text-white">{kind.title}</p>
          <p className="text-[11px] text-gray-500 leading-relaxed mt-0.5">{kind.desc}</p>
          {existing && (
            <p className="text-[11px] text-green-400 mt-2 flex items-center gap-1">
              <Check className="h-3 w-3" /> {existing.filename} <span className="text-gray-500">({Math.round(existing.bytes / 1024)} KB)</span>
              <button onClick={onRemove} className="ml-2 text-red-400 hover:underline" data-testid={`footprint-remove-${kind.id}`}>remove</button>
            </p>
          )}
        </div>
        {!existing && (
          <button onClick={() => ref.current?.click()} disabled={uploading} data-testid={`footprint-pick-${kind.id}`}
                  className="px-3 py-2 rounded-lg bg-[#00D4FF]/10 border border-[#00D4FF]/40 text-[#00D4FF] text-[11px] font-bold hover:bg-[#00D4FF]/20 transition-all flex items-center gap-1.5 flex-shrink-0">
            {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
            {uploading ? 'Uploading' : 'Upload'}
          </button>
        )}
        <input ref={ref} type="file" accept=".csv,.xlsx,.xls,.txt,.json" className="hidden" onChange={pick} />
      </div>
    </div>
  );
}

/* ═══════════════ STEP 4 — PRODUCTS & CORE AUDIENCE ═══════════════ */
function Step4Products({ products, setProducts, shoppableUrl, setShoppableUrl, utmBase, setUtmBase, pixelId, setPixelId }) {
  const fileInputRef = useRef(null);
  const [uploadMsg, setUploadMsg] = useState(null);
  const [manual, setManual] = useState({ sku: '', name: '', description: '', category: '' });
  const [expandedIdx, setExpandedIdx] = useState(null);

  const addManual = () => {
    if (!manual.sku.trim() || !manual.name.trim()) return;
    setProducts([...products, {
      ...manual,
      core_audience: { age_range: [18, 44], genders: [], geos: [] },
      competitors: [],
    }]);
    setManual({ sku: '', name: '', description: '', category: '' });
  };

  const onCsvPicked = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadMsg(null);
    try {
      const text = await f.text();
      const rows = parseCsv(text);
      setProducts([...products, ...rows.map(r => ({
        ...r,
        core_audience: { age_range: [18, 44], genders: [], geos: [] },
        competitors: [],
      }))]);
      setUploadMsg(`✓ Parsed ${rows.length} rows`);
    } catch (err) { setUploadMsg(`Parse error: ${err.message}`); }
  };

  const updateProduct = (i, field, val) => {
    const n = [...products]; n[i] = { ...n[i], [field]: val }; setProducts(n);
  };
  const updateAudience = (i, field, val) => {
    const n = [...products]; n[i] = { ...n[i], core_audience: { ...n[i].core_audience, [field]: val } }; setProducts(n);
  };

  return (
    <div className="space-y-7" data-testid="onboarding-step-4">
      <div>
        <h1 className="text-2xl font-black mb-1">Products & Core Audience</h1>
        <p className="text-sm text-gray-500">Each SKU gets its own Core Audience and competitor set — because brands rarely target the same buyer for every product.</p>
      </div>

      {/* CSV dropzone */}
      <div className="rounded-xl border-2 border-dashed border-[#1f1f1f] bg-[#0A0A0A] p-5 text-center hover:border-[#00D4FF] cursor-pointer transition-all"
           onClick={() => fileInputRef.current?.click()} data-testid="step4-catalog-dropzone">
        <FileSpreadsheet className="h-8 w-8 text-gray-600 mx-auto mb-2" />
        <p className="text-xs text-gray-400 font-semibold">Drop your product catalog CSV here, or click to browse</p>
        <p className="text-[10px] text-gray-600 mt-1">
          Columns: <span className="text-[#00D4FF]">sku, name, description, category</span> + optional <span className="text-[#00D4FF]">competitors</span> (pipe-separated) ·{' '}
          <a href="/wave_product_template.csv" download onClick={e => e.stopPropagation()} className="text-[#FF4D94] underline font-semibold">Download template</a>
        </p>
        {uploadMsg && <p className="text-[10px] mt-2 text-green-400">{uploadMsg}</p>}
        <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={onCsvPicked} data-testid="step4-catalog-input" />
      </div>

      {/* Manual add */}
      <div className="grid grid-cols-1 sm:grid-cols-[100px_1fr_1.5fr_120px_auto] gap-2">
        <input value={manual.sku} onChange={e => setManual({ ...manual, sku: e.target.value })} placeholder="SKU" className={inputCls} data-testid="step4-manual-sku" />
        <input value={manual.name} onChange={e => setManual({ ...manual, name: e.target.value })} placeholder="Name" className={inputCls} data-testid="step4-manual-name" />
        <input value={manual.description} onChange={e => setManual({ ...manual, description: e.target.value })} placeholder="Description (optional)" className={inputCls} />
        <input value={manual.category} onChange={e => setManual({ ...manual, category: e.target.value })} placeholder="Category" className={inputCls} />
        <button onClick={addManual} data-testid="step4-manual-add"
                className="px-4 rounded-lg text-xs whitespace-nowrap hover:brightness-110 transition-all flex items-center justify-center font-bold"
                style={{ background: 'transparent', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.4)' }}>
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>

      {/* Product table with per-SKU core audience */}
      {products.length > 0 && (
        <div className="rounded-xl bg-[#0A0A0A] border border-[#1f1f1f] overflow-hidden" data-testid="step4-product-table">
          <div className="flex items-center justify-between px-4 py-2 border-b border-[#1f1f1f]">
            <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold">{products.length} products · click row to set Core Audience</p>
            <button onClick={() => setProducts([])} className="text-[10px] text-red-400 hover:underline">Clear all</button>
          </div>
          <div className="max-h-[440px] overflow-y-auto divide-y divide-[#1f1f1f]">
            {products.map((prod, i) => (
              <ProductRow key={prod.sku || `prod_${i}`} i={i} prod={prod}
                          expanded={expandedIdx === i}
                          toggleExpand={() => setExpandedIdx(expandedIdx === i ? null : i)}
                          updateProduct={updateProduct}
                          updateAudience={updateAudience}
                          remove={() => setProducts(products.filter((_, idx) => idx !== i))} />
            ))}
          </div>
        </div>
      )}

      {/* Conversion tracking (brand-level) */}
      <Section title="Conversion Tracking" subtitle="Unlocks real ROAS (not simulated) once a wave runs. Brand-wide defaults — SKUs inherit." icon={Link2}>
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Shoppable URL">
            <input value={shoppableUrl} onChange={e => setShoppableUrl(e.target.value)} data-testid="step4-shoppable" placeholder="shop.brand.com/wave" className={inputCls} />
          </Field>
          <Field label="UTM Base">
            <input value={utmBase} onChange={e => setUtmBase(e.target.value)} data-testid="step4-utm" placeholder="wave-spring-2026" className={inputCls} />
          </Field>
          <Field label="Pixel ID (optional)">
            <input value={pixelId} onChange={e => setPixelId(e.target.value)} data-testid="step4-pixel" placeholder="e.g. Meta / TikTok pixel id" className={inputCls} />
          </Field>
        </div>
      </Section>
    </div>
  );
}

function ProductRow({ i, prod, expanded, toggleExpand, updateProduct, updateAudience, remove }) {
  const toggle = (list, val) => list.includes(val) ? list.filter(x => x !== val) : [...list, val];
  return (
    <div data-testid={`product-row-${i}`}>
      <button onClick={toggleExpand} className="w-full px-4 py-2.5 grid grid-cols-[70px_1fr_120px_80px_auto_auto] gap-3 text-[11px] items-center hover:bg-[#121212] transition-all text-left">
        <span className="text-[#00D4FF] font-mono">{prod.sku}</span>
        <span className="text-white truncate">{prod.name}</span>
        <span className="text-gray-500 truncate">{prod.category || '—'}</span>
        <span className={`text-[9px] uppercase tracking-wider ${prod.core_audience?.genders?.length || prod.core_audience?.geos?.length ? 'text-green-400' : 'text-gray-600'}`}>
          {prod.core_audience?.genders?.length || prod.core_audience?.geos?.length ? '✓ Audience' : 'Default'}
        </span>
        <span className={`text-[9px] uppercase tracking-wider ${prod.competitors?.length ? 'text-green-400' : 'text-gray-600'}`}>
          {prod.competitors?.length ? `${prod.competitors.length} comp` : 'No comp'}
        </span>
        <ChevronDown className={`h-3.5 w-3.5 text-gray-500 transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>
      {expanded && (
        <div className="px-4 pb-4 pt-2 space-y-4 bg-[#080808]" data-testid={`product-expand-${i}`}>
          <div className="grid sm:grid-cols-3 gap-3">
            <div className="rounded-lg p-3 bg-[#0A0A0A] border border-[#1f1f1f]">
              <p className="text-[9px] text-gray-500 uppercase tracking-wider font-bold mb-2">Age</p>
              <div className="flex items-center gap-2">
                <input type="number" value={prod.core_audience?.age_range?.[0] ?? 18}
                       onChange={e => updateAudience(i, 'age_range', [+e.target.value, prod.core_audience?.age_range?.[1] ?? 44])}
                       data-testid={`product-${i}-age-min`} className="w-14 px-2 py-1.5 rounded bg-[#121212] border border-[#1f1f1f] text-xs text-center outline-none" />
                <span className="text-gray-500 text-xs">to</span>
                <input type="number" value={prod.core_audience?.age_range?.[1] ?? 44}
                       onChange={e => updateAudience(i, 'age_range', [prod.core_audience?.age_range?.[0] ?? 18, +e.target.value])}
                       data-testid={`product-${i}-age-max`} className="w-14 px-2 py-1.5 rounded bg-[#121212] border border-[#1f1f1f] text-xs text-center outline-none" />
              </div>
            </div>
            <div className="rounded-lg p-3 bg-[#0A0A0A] border border-[#1f1f1f]">
              <p className="text-[9px] text-gray-500 uppercase tracking-wider font-bold mb-2">Genders</p>
              <div className="flex flex-wrap gap-1.5">
                {GENDERS.map(g => {
                  const active = prod.core_audience?.genders?.includes(g.id);
                  return (
                    <button key={g.id} onClick={() => updateAudience(i, 'genders', toggle(prod.core_audience?.genders || [], g.id))}
                            data-testid={`product-${i}-gender-${g.id}`}
                            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${active ? 'bg-[#FF4D94] text-white' : 'bg-[#121212] border border-[#1f1f1f] text-gray-400'}`}>
                      {g.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="rounded-lg p-3 bg-[#0A0A0A] border border-[#1f1f1f]">
              <p className="text-[9px] text-gray-500 uppercase tracking-wider font-bold mb-2">Geos</p>
              <div className="flex flex-wrap gap-1.5">
                {GEOS.map(g => {
                  const active = prod.core_audience?.geos?.includes(g);
                  return (
                    <button key={g} onClick={() => updateAudience(i, 'geos', toggle(prod.core_audience?.geos || [], g))}
                            data-testid={`product-${i}-geo-${g}`}
                            className={`px-2.5 py-1 rounded-full text-[10px] font-semibold ${active ? 'bg-[#00D4FF] text-black' : 'bg-[#121212] border border-[#1f1f1f] text-gray-400'}`}>
                      {g}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <CompetitorsTagInput value={prod.competitors || []} onChange={(comp) => updateProduct(i, 'competitors', comp)} testId={`product-${i}-competitors`} />

          <div className="flex justify-end">
            <button onClick={remove} data-testid={`product-remove-${i}`}
                    className="text-[10px] text-red-400 hover:underline flex items-center gap-1">
              <Trash2 className="h-3 w-3" /> Remove product
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CompetitorsTagInput({ value, onChange, testId }) {
  const [draft, setDraft] = useState('');
  const add = () => {
    const t = draft.trim();
    if (!t || value.includes(t)) return;
    onChange([...value, t]); setDraft('');
  };
  return (
    <div className="rounded-lg p-3 bg-[#0A0A0A] border border-[#1f1f1f]">
      <p className="text-[9px] text-gray-500 uppercase tracking-wider font-bold mb-2">Competitors for this product</p>
      <div className="flex flex-wrap gap-1.5 mb-2">
        {value.map(c => (
          <span key={c} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-[#FF4D94]/15 border border-[#FF4D94]/30 text-[#FF4D94]">
            {c}
            <button onClick={() => onChange(value.filter(x => x !== c))} className="hover:text-white"><X className="h-2.5 w-2.5" /></button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), add())}
               data-testid={testId}
               placeholder="Type a competitor brand and press Enter" className={inputCls} />
        <button onClick={add}
                className="px-3 rounded-lg text-xs whitespace-nowrap hover:brightness-110 font-bold"
                style={{ background: 'transparent', color: '#00D4FF', border: '1px solid rgba(0,212,255,0.4)' }}>
          <Plus className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length < 2) throw new Error('CSV needs a header row + at least one data row');
  const header = lines[0].toLowerCase().split(',').map(s => s.trim().replace(/^"|"$/g, ''));
  const idx = (k) => header.indexOf(k);
  const out = [];
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].match(/(".*?"|[^",]+)(?=\s*,|\s*$)/g)?.map(c => c.trim().replace(/^"|"$/g, '')) || [];
    const compRaw = (cols[idx('competitors')] || '').trim();
    const row = {
      sku: cols[idx('sku')] || cols[idx('id')] || '',
      name: cols[idx('name')] || cols[idx('product')] || '',
      description: cols[idx('description')] || '',
      category: cols[idx('category')] || '',
      competitors: compRaw ? compRaw.split('|').map(s => s.trim()).filter(Boolean) : [],
    };
    if (row.sku && row.name) out.push(row);
  }
  return out;
}

/* ═══════════════ STEP 5 — SUBSCRIPTION & WALLET ═══════════════ */
function Step5Sub({ plans, feeRate, chosenTier, setChosenTier, chosenTopup, setChosenTopup, customAmount, setCustomAmount, subscribe, topup, subscription, walletBalance }) {
  const isSubscribed = subscription === 'active';
  return (
    <div className="space-y-6" data-testid="onboarding-step-5">
      <div>
        <h1 className="text-2xl font-black mb-1">Subscribe & fund your wallet</h1>
        <p className="text-sm text-gray-500">Pick a subscription tier, then top up your wallet. Your wallet funds every Wave you launch.</p>
      </div>
      {!isSubscribed && (
        <div className="grid sm:grid-cols-3 gap-3">
          {(plans?.subscriptions || []).map(t => (
            <div key={t.id} className={`rounded-2xl p-5 border-2 transition-all ${chosenTier === t.id ? 'border-[#FF4D94]' : 'border-[#1f1f1f]'} bg-[#0A0A0A] relative overflow-hidden`} data-testid={`step5-plan-${t.id}`}>
              {t.id === 'pro' && <span className="absolute top-3 right-3 text-[8px] font-bold bg-[#00D4FF] text-black rounded-full px-2 py-0.5">POPULAR</span>}
              <p className="text-xs text-gray-400 uppercase tracking-widest font-bold">{t.name}</p>
              <p className="text-3xl font-black text-white mt-2">${t.amount}<span className="text-xs text-gray-500 font-semibold">/mo</span></p>
              <p className="text-[11px] text-gray-500 mt-2 leading-snug">{t.description}</p>
              <button onClick={() => { setChosenTier(t.id); subscribe(t.id); }} data-testid={`step5-subscribe-${t.id}`}
                      className="mt-4 w-full py-2.5 rounded-lg bg-[#FF4D94] text-white font-bold text-xs hover:brightness-110 hover:scale-[1.02] transition-all flex items-center justify-center gap-1.5"
                      style={{ boxShadow: '0 0 18px rgba(255,77,148,0.3)' }}>
                <CreditCard className="h-3.5 w-3.5" /> Subscribe
              </button>
            </div>
          ))}
        </div>
      )}
      {isSubscribed && (
        <div className="rounded-xl p-4 bg-[#00D4FF]/5 border border-[#00D4FF]/25 flex items-center gap-3" data-testid="step5-subscribed">
          <Check className="h-5 w-5 text-[#00D4FF]" />
          <div className="flex-1">
            <p className="text-xs font-bold text-white">Subscription active</p>
            <p className="text-[10px] text-gray-500">Now top up your wallet to launch your first Wave.</p>
          </div>
        </div>
      )}
      <div>
        <p className="text-[10px] uppercase tracking-widest text-gray-500 font-bold mb-3 flex items-center gap-1.5"><Wallet className="h-3 w-3" /> Top up your wallet</p>
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5">
          {(plans?.wallet_packages || []).map(pkg => (
            <button key={pkg.id} onClick={() => { setChosenTopup(pkg.id); setCustomAmount(''); }}
                    data-testid={`step5-topup-${pkg.id}`}
                    className={`py-4 rounded-xl border-2 text-center transition-all ${chosenTopup === pkg.id ? 'border-[#00D4FF] bg-[#00D4FF]/10' : 'border-[#1f1f1f] bg-[#0A0A0A] hover:border-[#00D4FF]'}`}>
              <p className="text-base font-black text-white">{pkg.label}</p>
              <p className="text-[9px] text-gray-500 mt-0.5">one-time</p>
            </button>
          ))}
        </div>
        <div className="mt-3 flex gap-2">
          <input type="number" placeholder="Custom amount (USD)" value={customAmount}
                 onChange={e => { setCustomAmount(e.target.value); setChosenTopup(null); }} data-testid="step5-custom-amount" className={inputCls} />
          <button onClick={topup} data-testid="step5-topup-btn"
                  disabled={!chosenTopup && !customAmount}
                  className="px-5 py-3 rounded-lg bg-[#FF4D94] text-white font-bold text-xs disabled:opacity-50 hover:brightness-110 hover:scale-[1.02] transition-all flex items-center gap-2"
                  style={{ boxShadow: '0 0 18px rgba(255,77,148,0.3)' }}>
            <CreditCard className="h-3.5 w-3.5" /> Top up via Stripe
          </button>
        </div>
        <p className="mt-3 text-[10px] text-gray-500 leading-relaxed">
          Current wallet: <span className="text-[#00D4FF] font-bold">${(walletBalance || 0).toLocaleString()}</span>. Top up any time from Billing — you'll always see the total you're committing before a Wave launches.
        </p>
      </div>
    </div>
  );
}

/* ═══════════════ STEP 6 — TEAM & CONFIRM ═══════════════ */
function Step6Team({ invites, setInvites, summary, user }) {
  const updateInvite = (i, field, val) => { const n = [...invites]; n[i] = { ...n[i], [field]: val }; setInvites(n); };
  const add = () => setInvites([...invites, { _rid: makeRowId(), email: '', role: 'brand_manager' }]);
  const remove = (i) => setInvites(invites.filter((_, idx) => idx !== i));
  return (
    <div className="space-y-6" data-testid="onboarding-step-6">
      <div>
        <div className="w-12 h-12 rounded-full bg-[#FF4D94]/15 flex items-center justify-center mb-3">
          <Sparkles className="h-6 w-6 text-[#FF4D94]" />
        </div>
        <h1 className="text-2xl font-black mb-1">Invite your team & review</h1>
        <p className="text-sm text-gray-500">Add teammates (optional) and confirm your setup.</p>
      </div>

      <Section title="Add Team Members" icon={UserPlus}>
        <div className="space-y-2">
          {invites.map((inv, i) => (
            <div key={inv._rid || i} className="grid grid-cols-[1fr_180px_auto] gap-2 items-center" data-testid={`invite-row-${i}`}>
              <div className="flex items-center gap-1.5 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] focus-within:border-[#00D4FF] px-3">
                <Mail className="h-3 w-3 text-gray-600" />
                <input value={inv.email} onChange={e => updateInvite(i, 'email', e.target.value)} data-testid={`invite-email-${i}`}
                       placeholder="teammate@brand.com" className="bg-transparent outline-none text-sm py-2.5 w-full" />
              </div>
              <select value={inv.role} onChange={e => updateInvite(i, 'role', e.target.value)} data-testid={`invite-role-${i}`} className={inputCls}>
                {TEAM_ROLES.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
              <button onClick={() => remove(i)} className="w-10 h-10 rounded-lg bg-[#0A0A0A] border border-[#1f1f1f] hover:border-red-500/50 text-red-400 flex items-center justify-center" data-testid={`invite-remove-${i}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
        <button type="button" onClick={add} data-testid="invite-add"
                className="mt-2 w-full py-2.5 rounded-lg border-2 border-dashed border-[#1f1f1f] hover:border-[#00D4FF] hover:bg-[#00D4FF]/5 text-xs font-semibold text-gray-400 hover:text-white flex items-center justify-center gap-2 transition-all">
          <Plus className="h-3.5 w-3.5" /> Add another teammate
        </button>
      </Section>

      <div className="rounded-xl bg-[#0A0A0A] border border-[#1f1f1f] p-5" data-testid="summary-card">
        <div className="flex items-center gap-4 mb-4">
          {summary.logoPreview
            ? <img src={summary.logoPreview} alt="Logo" className="w-14 h-14 rounded-full object-cover" />
            : <div className="w-14 h-14 rounded-full bg-[#00D4FF]/15 flex items-center justify-center"><Waves className="h-6 w-6 text-[#00D4FF]" /></div>}
          <div>
            <p className="text-base font-black text-white">{summary.brandName || user?.brand_name}</p>
            <p className="text-[11px] text-gray-400">
              {summary.vertical}
              {summary.parentCompany ? ` · part of ${summary.parentCompany}` : ''}
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          {summary.subBrands.length > 0 && <SummaryRow label="Sub-brands" value={`${summary.subBrands.length} in portfolio`} color="#00D4FF" />}
          <SummaryRow label="Products" value={`${summary.products.length} SKUs with per-SKU audience`} color={summary.products.length > 0 ? '#4ade80' : '#a1a1aa'} />
          <SummaryRow label="1st Party Data" value={summary.footprintAssets?.length ? `${summary.footprintAssets.length} of 6 uploaded` : 'Not uploaded'} color={summary.footprintAssets?.length ? '#4ade80' : '#a1a1aa'} />
          <SummaryRow label="Attribution" value={summary.shoppableUrl || summary.utmBase || summary.pixelId ? 'Live ROAS ready' : 'Simulated ROAS only'} color={summary.shoppableUrl || summary.utmBase ? '#4ade80' : '#a1a1aa'} />
          <SummaryRow label="Subscription" value={user?.subscription_status === 'active' ? `${user.subscription_tier?.toUpperCase()} — Active` : 'Not subscribed'} color={user?.subscription_status === 'active' ? '#4ade80' : '#a1a1aa'} />
          <SummaryRow label="Wallet" value={`$${Number(user?.wallet_balance || 0).toLocaleString()}`} color="#00D4FF" />
        </div>
      </div>

      <div className="rounded-xl p-4 bg-gradient-to-r from-[#FF4D94]/10 to-[#00D4FF]/10 border border-[#FF4D94]/25 flex items-center gap-3">
        <Sparkles className="h-5 w-5 text-[#FF4D94]" />
        <p className="text-xs text-white">Your Digital DNA™ + Footprint is built. The next wave you plan matches against it automatically.</p>
      </div>
    </div>
  );
}

function SummaryRow({ label, value, color }) {
  return (
    <div>
      <p className="text-[9px] uppercase tracking-widest text-gray-600 font-bold">{label}</p>
      <p className="text-sm font-semibold mt-1" style={{ color: color || '#fff' }}>{value}</p>
    </div>
  );
}
